import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ArticleBlocks } from "./ArticleBlocks";
import { ShareBar } from "./ShareBar";
import { useCovers } from "./useCovers";
import { BlogLayout } from "./BlogLayout";
import { ArticleGrid, fmtIt } from "./ArticleCard";
import { useSeo } from "./useSeo";
import { ARTICLES, articleBySlug } from "@/content/articles";
import { MaskLine, Reveal } from "@/components/landing/Reveal";
import { WHATSAPP_URL } from "@/components/landing/data";

const SITE = "https://centrodiparodontologia.com";

const Cta = () => (
  <div className="mt-16 rounded-[2rem] bg-[#0F2317] text-[#FCFBF7] p-8 sm:p-10" data-testid="article-cta">
    <p className="eyebrow text-[#86C89B]">Prima visita</p>
    <p className="f-serif text-4xl leading-[1.05] mt-3">Hai riconosciuto qualche segnale?<br /><em className="text-[#86C89B]">Parliamone con calma.</em></p>
    <div className="mt-8 flex flex-wrap gap-3">
      <Link to="/#prenota" className="rounded-full bg-[#86C89B] text-[#0F2317] px-6 py-3.5 text-sm hover:bg-[#FCFBF7] transition-colors" data-testid="article-cta-booking">Prenota la prima visita</Link>
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="rounded-full border border-[#FCFBF7]/25 px-6 py-3.5 text-sm hover:bg-[#FCFBF7]/10 transition-colors" data-testid="article-cta-whatsapp">Scrivici su WhatsApp</a>
    </div>
  </div>
);

export default function ArticlePage() {
  const { slug } = useParams();
  const a = articleBySlug(slug);
  const cover = useCovers()[slug];
  const image = a?.cover ? `${SITE}${a.cover}` : cover?.startsWith("https://") ? cover : `${SITE}/og-image.jpg`;
  const jsonLd = useMemo(() => a && ({
    "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.description,
    datePublished: a.date, dateModified: a.date, inLanguage: "it-IT", image,
    mainEntityOfPage: `${SITE}/blog/${a.slug}`,
    author: { "@type": "Person", name: "Dott. Nicola Mauro" },
    publisher: { "@type": "Organization", name: "Centro di Parodontologia", logo: { "@type": "ImageObject", url: `${SITE}/favicon.svg` } },
  }), [a, image]);
  useSeo({ title: a ? `${a.title} | Centro di Parodontologia` : "Articolo non trovato", description: a?.description || "", path: `/blog/${slug}`, jsonLd, image, type: "article" });

  if (!a) return <BlogLayout><p className="mx-auto max-w-3xl px-6 py-32 f-serif text-4xl text-[#1B3B28]" data-testid="article-not-found">Articolo non trovato. <Link to="/blog" className="underline">Torna al blog</Link></p></BlogLayout>;
  const others = ARTICLES.filter((x) => x.slug !== a.slug).slice(0, 3);

  return (
    <BlogLayout>
      <article className="mx-auto max-w-3xl px-6 pt-16 pb-20" data-testid="article-page">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#526356] hover:text-[#1B3B28]" data-testid="article-back-link"><ArrowLeft size={15} /> Tutti gli articoli</Link>
        <p className="eyebrow text-[#3E6B48] mt-10">{a.category} · {a.readTime} min</p>
        <h1 className="f-serif text-4xl sm:text-5xl lg:text-6xl text-[#1B3B28] leading-[1] mt-4" data-testid="article-title"><MaskLine>{a.title}</MaskLine></h1>
        <p className="text-sm text-[#526356] mt-6">Dott. Nicola Mauro · {fmtIt(a.date)}</p>
        <ShareBar a={a} cover={cover} />
        {cover && (
          <Reveal className="mt-10">
            <div className="relative aspect-[16/10] rounded-t-[12rem] rounded-b-[2rem] overflow-hidden shadow-[0_40px_80px_-40px_rgba(27,59,40,0.5)]" data-testid="article-cover">
              <img src={cover} alt={a.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        )}
        <Reveal><p className="f-serif text-2xl text-[#1E2922] leading-snug mt-10 italic">{a.intro}</p></Reveal>
        {a.sections.map((s) => (
          <Reveal key={s.h}>
            <section className="mt-10">
              <h2 className="f-serif text-3xl text-[#1B3B28]">{s.h}</h2>
              {s.p.length > 2
                ? <ul className="mt-4 space-y-3">{s.p.map((t) => <li key={t} className="flex gap-3 text-[#1E2922]/85 leading-relaxed"><span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#3E6B48] shrink-0" />{t}</li>)}</ul>
                : s.p.map((t) => <p key={t} className="mt-4 text-[#1E2922]/85 leading-relaxed">{t}</p>)}
              <ArticleBlocks s={s} />
            </section>
          </Reveal>
        ))}
        <p className="mt-12 text-xs text-[#526356] border-t border-[#1B3B28]/10 pt-6">Le informazioni hanno scopo divulgativo e non sostituiscono una visita specialistica, che verrà effettuata con un odontoiatra regolarmente iscritto all'albo.</p>
        <ShareBar a={a} cover={cover} place="bottom" />
        <Cta />
      </article>
      <section className="mx-auto max-w-[1400px] px-6 sm:px-10 pb-28">
        <p className="f-serif text-4xl text-[#1B3B28] mb-8">Continua a leggere</p>
        <ArticleGrid items={others} />
      </section>
    </BlogLayout>
  );
}
